import type { LucideIcon } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn';

/** Menu rozwijane (Radix DropdownMenu): strzałki, Enter, Esc, pisanie = skok do pozycji. */
export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;

export function MenuContent({
  className,
  align = 'start',
  sideOffset = 6,
  ...props
}: ComponentProps<typeof DropdownMenu.Content>) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-50 min-w-[200px] rounded-lg border border-line bg-canvas p-1 shadow-popover animate-pop-in',
          className,
        )}
        {...props}
      />
    </DropdownMenu.Portal>
  );
}

export function MenuItem({
  icon: Icon,
  shortcut,
  children,
  className,
  tone = 'default',
  ...props
}: ComponentProps<typeof DropdownMenu.Item> & {
  icon?: LucideIcon;
  shortcut?: ReactNode;
  tone?: 'default' | 'danger';
}) {
  return (
    <DropdownMenu.Item
      className={cn(
        'flex h-8 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-body-s outline-none select-none',
        'data-[highlighted]:bg-elevated data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        tone === 'danger' ? 'text-danger' : 'text-fg',
        className,
      )}
      {...props}
    >
      {Icon && (
        <Icon
          size={16}
          strokeWidth={1.75}
          aria-hidden
          className={tone === 'danger' ? 'text-danger' : 'text-fg-muted'}
        />
      )}
      <span className="flex-1 truncate">{children}</span>
      {shortcut && <span className="text-caption text-fg-muted">{shortcut}</span>}
    </DropdownMenu.Item>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <DropdownMenu.Label className="px-2.5 pt-2 pb-1 text-caption font-medium text-fg-muted">
      {children}
    </DropdownMenu.Label>
  );
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="my-1 h-px bg-line" />;
}

export const MenuRadioGroup = DropdownMenu.RadioGroup;

export function MenuRadioItem({ children, className, ...props }: ComponentProps<typeof DropdownMenu.RadioItem>) {
  return (
    <DropdownMenu.RadioItem
      className={cn(
        'flex h-8 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-body-s text-fg outline-none select-none',
        'data-[highlighted]:bg-elevated data-[state=checked]:font-medium',
        className,
      )}
      {...props}
    >
      <span className="flex-1 truncate">{children}</span>
      <DropdownMenu.ItemIndicator>
        <span aria-hidden className="block size-1.5 rounded-full bg-accent" />
      </DropdownMenu.ItemIndicator>
    </DropdownMenu.RadioItem>
  );
}
